import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Flame, Target, Repeat, Clock4, Users, Quote, LifeBuoy, Loader2 } from "lucide-react";

import TopBar from "../components/TopBar";
import TranscriptPanel from "../components/TranscriptPanel";
import CategoryCard from "../components/CategoryCard";
import MarkMomentButton from "../components/MarkMomentButton";
import MarkedMomentsList from "../components/MarkedMomentsList";
import PreFlightCheck from "../components/PreFlightCheck";
import SummaryOverlay from "../components/SummaryOverlay";
import PhaseTracker from "../components/PhaseTracker";
import DiscoveryTracker from "../components/DiscoveryTracker";
import HelpModal from "../components/HelpModal";
import CompactCloserView from "../components/CompactCloserView";
import PipView from "../components/PipView";
import { getSuggestedNextPhase } from "../lib/guidance";
import { track, PIP_EVENTS, isPipSupported, isPipModeEnabled } from "../lib/analytics";

import {
  ChunkedRecorder,
  getMicStream,
  getSystemAudioStream,
} from "../lib/recorder";
import {
  createSession,
  uploadAudioChunk,
  markMoment as apiMarkMoment,
  endSession as apiEndSession,
} from "../lib/api";

const CHUNK_MS = 5000;

const emptyState = () => ({
  pain_points: [],
  goals: [],
  previous_attempts: [],
  cost_of_inaction: [],
  decision_maker: [],
  key_quotes: [],
  financial_situation: [],
  marked_moments: [],
});

export default function Dashboard() {
  const [status, setStatus] = useState("idle"); // idle | starting | live | summarizing
  const [sessionId, setSessionId] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [state, setState] = useState(emptyState());
  const [elapsed, setElapsed] = useState(0);
  const [micOn, setMicOn] = useState(false);
  const [sysOn, setSysOn] = useState(false);
  const [summaryText, setSummaryText] = useState(null);
  const [summarySession, setSummarySession] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [preflightReady, setPreflightReady] = useState(false);
  const [phase, setPhase] = useState("upfront");
  const [helpOpen, setHelpOpen] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [extractionReady, setExtractionReady] = useState(false);
  const [pipWindow, setPipWindow] = useState(null);
  const pipEnabled = isPipModeEnabled();
  // V0.3.11 — B2B/B2C discovery orientation (guidance only, not workflow).
  // Persisted so the closer's preference survives refreshes across calls.
  const [mode, setMode] = useState(() => {
    try {
      const v = localStorage.getItem("sch.mode");
      return v === "b2c" ? "b2c" : "b2b";
    } catch (e) {
      return "b2b";
    }
  });
  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === "b2c" ? "b2b" : "b2c";
      try { localStorage.setItem("sch.mode", next); } catch (e) { /* noop */ }
      return next;
    });
  };

  const startedAtRef = useRef(null);
  const micRecRef = useRef(null);
  const sysRecRef = useRef(null);
  const sessionIdRef = useRef(null);
  const inflightRef = useRef(0);

  useEffect(() => {
    if (status !== "live") return;
    const t = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 500);
    return () => clearInterval(t);
  }, [status]);

  const handleChunk = useCallback(async (blob, speaker) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    const ts = (Date.now() - startedAtRef.current) / 1000;
    inflightRef.current += 1;
    try {
      const data = await uploadAudioChunk(sid, blob, speaker, ts);
      if (data?.new_line) {
        setTranscript((prev) => [...prev, data.new_line]);
      }
      if (typeof data?.extraction_ready === "boolean") {
        setExtractionReady(data.extraction_ready);
      }
      setState((prev) => ({
        ...prev,
        pain_points: data.pain_points ?? prev.pain_points,
        goals: data.goals ?? prev.goals,
        previous_attempts: data.previous_attempts ?? prev.previous_attempts,
        cost_of_inaction: data.cost_of_inaction ?? prev.cost_of_inaction,
        decision_maker: data.decision_maker ?? prev.decision_maker,
        key_quotes: data.key_quotes ?? prev.key_quotes,
        financial_situation: data.financial_situation ?? prev.financial_situation,
      }));
    } catch (err) {
      console.error("upload failed", err);
    } finally {
      inflightRef.current -= 1;
    }
  }, []);

  const start = async () => {
    if (status !== "idle") return;
    setStatus("starting");

    let micStream = null;
    let sysStream = null;

    try {
      micStream = await getMicStream();
      setMicOn(true);
    } catch (e) {
      toast.error("Microphone permission denied. The closer audio won't be captured.");
    }

    try {
      sysStream = await getSystemAudioStream();
      setSysOn(true);
    } catch (e) {
      toast.warning(
        "Prospect audio not captured. When the picker appears, choose the meeting tab AND tick 'Share tab audio'."
      );
    }

    if (!micStream && !sysStream) {
      setStatus("idle");
      return;
    }

    let session;
    try {
      session = await createSession(null);
    } catch (e) {
      toast.error("Could not create session. Is the backend running?");
      micStream?.getTracks().forEach((t) => t.stop());
      sysStream?.getTracks().forEach((t) => t.stop());
      setMicOn(false);
      setSysOn(false);
      setStatus("idle");
      return;
    }

    setSessionId(session.id);
    sessionIdRef.current = session.id;
    setTranscript([]);
    setState(emptyState());
    setElapsed(0);
    startedAtRef.current = Date.now();
    setStatus("live");
    toast.success("Listening started. Focus on the conversation — SCH is taking notes.");

    if (micStream) {
      const rec = new ChunkedRecorder(micStream, CHUNK_MS, (blob) => handleChunk(blob, "closer"));
      rec.start();
      micRecRef.current = rec;
    }
    if (sysStream) {
      const rec = new ChunkedRecorder(sysStream, CHUNK_MS, (blob) => handleChunk(blob, "prospect"));
      rec.start();
      sysRecRef.current = rec;
    }
  };

  const stop = async () => {
    if (status !== "live") return;
    setStatus("summarizing");
    setShowOverlay(true);
    setSummaryText(null);
    micRecRef.current?.stop();
    sysRecRef.current?.stop();
    micRecRef.current = null;
    sysRecRef.current = null;
    setMicOn(false);
    setSysOn(false);

    // Wait briefly for any in-flight chunks to finish posting so the summary
    // sees the latest transcript.
    const waitStart = Date.now();
    while (inflightRef.current > 0 && Date.now() - waitStart < 8000) {
      await new Promise((r) => setTimeout(r, 300));
    }

    const sid = sessionIdRef.current;
    try {
      const result = await apiEndSession(sid);
      setSummaryText(result.summary || "");
      setSummarySession({ id: sid, title: `Call ${new Date().toLocaleString()}` });
      // V0.3.9: source-of-truth contract. /end returns the fully-reconciled
      // category arrays after full-transcript reconciliation → per-category
      // consolidation → pairwise reconcile. REPLACE stale live state so the
      // read-only post-call dashboard shows the cleaned cards, not pre-
      // consolidation duplicates.
      setState((prev) => ({
        ...prev,
        pain_points: result.pain_points ?? prev.pain_points,
        goals: result.goals ?? prev.goals,
        previous_attempts: result.previous_attempts ?? prev.previous_attempts,
        cost_of_inaction: result.cost_of_inaction ?? prev.cost_of_inaction,
        decision_maker: result.decision_maker ?? prev.decision_maker,
        key_quotes: result.key_quotes ?? prev.key_quotes,
        financial_situation: result.financial_situation ?? prev.financial_situation,
      }));
      toast.success("Summary ready");
    } catch (e) {
      toast.error("Failed to generate summary.");
      setShowOverlay(false);
      setStatus("idle");
    }
  };

  const resetForNewCall = () => {
    setShowOverlay(false);
    setSummaryText(null);
    setSummarySession(null);
    setSessionId(null);
    sessionIdRef.current = null;
    setTranscript([]);
    setState(emptyState());
    setElapsed(0);
    setExtractionReady(false);
    setPhase("upfront");
    setStatus("idle");
  };

  const returnToDashboard = () => {
    // Keep the last-call's extracted state visible (read-only review) but close
    // the overlay so the closer can scan their categories without distraction.
    setShowOverlay(false);
    setStatus("idle");
  };

  const markMoment = async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    const ts = (Date.now() - startedAtRef.current) / 1000;
    try {
      const data = await apiMarkMoment(sid, ts, 15);
      setState((prev) => ({
        ...prev,
        marked_moments: [...prev.marked_moments, data.moment],
        key_quotes: data.key_quotes ?? prev.key_quotes,
      }));
      toast.success("Moment marked ⭐");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Need at least a few seconds of clear prospect speech.";
      toast.warning(msg);
    }
  };

  // --- PiP overlay (Sprint 1, behind REACT_APP_ENABLE_PIP_MODE flag) ---
  const openPip = useCallback(async () => {
    track(PIP_EVENTS.POP_OUT_CLICKED);
    if (!pipEnabled) return;

    // Native Document PiP path (Chrome/Edge/Brave/Arc/Opera).
    if (isPipSupported()) {
      try {
        const win = await window.documentPictureInPicture.requestWindow({
          width: 340,
          height: 240,
        });
        // Copy every parent stylesheet into the PiP window so Tailwind + our
        // theme render identically. Style copy uses <link> where possible so
        // future style changes hot-reload; falls back to inline CSS blobs.
        [...document.styleSheets].forEach((sheet) => {
          try {
            const rules = [...sheet.cssRules].map((r) => r.cssText).join("\n");
            const el = win.document.createElement("style");
            el.textContent = rules;
            win.document.head.appendChild(el);
          } catch (e) {
            if (sheet.href) {
              const link = win.document.createElement("link");
              link.rel = "stylesheet";
              link.href = sheet.href;
              win.document.head.appendChild(link);
            }
          }
        });
        win.document.body.style.margin = "0";
        win.document.body.style.background = "#f7f7f5";
        win.addEventListener("pagehide", () => {
          setPipWindow(null);
          track(PIP_EVENTS.PIP_CLOSED_MANUALLY);
        });
        setPipWindow(win);
        track(PIP_EVENTS.PIP_OPENED, { mode: "document-pip" });
        return;
      } catch (e) {
        track(PIP_EVENTS.PIP_OPEN_FAILED, { error: String(e) });
      }
    }

    // Fallback for Firefox / Safari — plain small popup window. Won't float
    // above full-screen apps but is meaningfully better than a browser tab.
    try {
      const url = `${window.location.origin}/pip?session=${sessionIdRef.current || ""}`;
      const w = window.open(url, "sch-pip", "width=340,height=240,alwaysRaised=yes");
      if (w) {
        track(PIP_EVENTS.PIP_FALLBACK_WINDOW);
      }
    } catch (e) {
      track(PIP_EVENTS.PIP_OPEN_FAILED, { error: String(e) });
      toast.error("Could not open floating window.");
    }
  }, [pipEnabled]);

  // Auto-close PiP when the call ends so we don't leave a stale window open.
  useEffect(() => {
    if (status === "summarizing" && pipWindow) {
      try {
        pipWindow.close();
      } catch (e) { /* ignore */ }
      setPipWindow(null);
    }
  }, [status, pipWindow]);

  const suggestedNext = useMemo(
    () => getSuggestedNextPhase({ phase, state, transcript }),
    [phase, state, transcript]
  );

  // V0.3.4 guardrail: ONLY Upfront → Discovery auto-advances. Every later
  // transition stays manual (pulse-only) so the closer is never surprised by
  // a phase swap mid-call.
  useEffect(() => {
    if (phase === "upfront" && transcript.length >= 4) {
      setPhase("discovery");
    }
  }, [phase, transcript.length]);

  const isLive = status === "live";
  const waitingForSpeech = isLive && transcript.length > 0 && !extractionReady;

  return (
    <div className="min-h-screen flex flex-col">
      <TopBar
        status={status === "live" ? "live" : status === "starting" ? "starting" : status === "summarizing" ? "summarizing" : "idle"}
        elapsed={elapsed}
        micOn={micOn}
        sysOn={sysOn}
        onStart={start}
        onStop={stop}
        compactMode={compactMode}
        onToggleCompact={() => setCompactMode((v) => !v)}
        mode={mode}
        onToggleMode={toggleMode}
      />

      {compactMode ? (
        <main className="flex-1 px-6 py-6">
          <CompactCloserView
            phase={phase}
            state={state}
            markMoment={markMoment}
            onOpenHelp={() => setHelpOpen(true)}
            onExpand={() => setCompactMode(false)}
            onPopOut={pipEnabled ? openPip : null}
            isLive={isLive}
          />
        </main>
      ) : (
        <main className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-6 p-6 max-w-[1600px] w-full mx-auto">
        <TranscriptPanel lines={transcript} />

        <section className="xl:col-span-7 flex flex-col gap-4 min-h-0">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <PhaseTracker phase={phase} onChange={setPhase} pulse={suggestedNext} />
            </div>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              data-testid="i-need-help-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-3 py-1.5 text-xs font-medium transition-colors"
              title="Show prompts for the current phase"
            >
              <LifeBuoy className="h-3.5 w-3.5" />
              I Need Help
            </button>
          </div>

          {waitingForSpeech && (
            <div
              data-testid="waiting-for-speech-banner"
              className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3 text-blue-900"
            >
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              <div className="text-sm leading-snug">
                <span className="font-medium">Waiting for meaningful conversation…</span>
                <span className="ml-1 text-blue-700/80">
                  Extraction starts once enough prospect speech has been captured.
                </span>
              </div>
            </div>
          )}

          <MarkMomentButton onMark={markMoment} disabled={status !== "live"} />

          {phase === "discovery" && <DiscoveryTracker state={state} />}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CategoryCard
              testId="card-pain-points"
              tone="pain"
              title="Pain Points"
              icon={Flame}
              items={state.pain_points}
              showQuote
            />
            <CategoryCard
              testId="card-goals"
              tone="goal"
              title="Goals"
              icon={Target}
              items={state.goals}
              showQuote
            />
            <CategoryCard
              testId="card-previous-attempts"
              tone="neutral"
              title="Previous Attempts"
              icon={Repeat}
              items={state.previous_attempts}
            />
            <CategoryCard
              testId="card-cost-of-inaction"
              tone="cost"
              title="Cost of Inaction"
              icon={Clock4}
              items={state.cost_of_inaction}
              showQuote
            />
            <CategoryCard
              testId="card-decision-maker"
              tone="neutral"
              title="Decision Maker"
              icon={Users}
              items={state.decision_maker}
            />
            <CategoryCard
              testId="card-key-quotes"
              tone="neutral"
              title="Key Quotes"
              icon={Quote}
              items={state.key_quotes}
              showQuote
            />
          </div>

          <MarkedMomentsList moments={state.marked_moments} />

          {status === "idle" && transcript.length === 0 && (
            <PreFlightCheck onAllReady={setPreflightReady} />
          )}
        </section>
      </main>
      )}

      {showOverlay && (
        <SummaryOverlay
          phase={summaryText == null ? "loading" : "ready"}
          summary={summaryText}
          session={summarySession}
          onStartNew={resetForNewCall}
          onReturnToDashboard={returnToDashboard}
        />
      )}

      <HelpModal
        open={helpOpen}
        onOpenChange={setHelpOpen}
        phase={phase}
        state={state}
        transcript={transcript}
        mode={mode}
      />

      {pipWindow &&
        createPortal(
          <PipView
            phase={phase}
            state={state}
            transcript={transcript}
            mode={mode}
            isLive={isLive}
            onSetPhase={setPhase}
            onMark={markMoment}
            onExpand={() => {
              // Keep the PiP window OPEN — just bring the main dashboard tab
              // to the foreground so the closer can drive from either surface.
              track(PIP_EVENTS.EXPAND_CLICKED);
              setCompactMode(false);
              try { window.focus(); } catch (e) { /* ignore */ }
            }}
          />,
          pipWindow.document.body
        )}
    </div>
  );
}
